import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { BatchRecord, BatchRecordsState } from '../../types/batchRecord';
import { batchRecordApiService } from '../../services/batchRecordApiService';
import { batchRecordFillingBatchApiService } from '../../services/batchRecordFillingBatchApiService';
import { batchRecordDoughBatchApiService } from '../../services/batchRecordDoughBatchApiService';

// Helper function to create a blank batch record
export const createBlankBatchRecord = (): BatchRecord => {
  const today = new Date().toISOString().split('T')[0];
  return {
    id: uuidv4(),
    productName: '',
    productId: '',
    dateOfProduction: today,
    batchLotNumber: '',
    preparedBy: '',
    verifiedBy: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
};

// Async thunks

/**
 * Fetch all batch records from the API
 */
export const fetchBatchRecords = createAsyncThunk(
  'batchRecords/fetchBatchRecords',
  async () => {
    return await batchRecordApiService.fetchBatchRecords();
  }
);

/**
 * Create a new batch record
 */
export const createBatchRecordAsync = createAsyncThunk(
  'batchRecords/createBatchRecord',
  async (batchRecord: BatchRecord) => {
    return await batchRecordApiService.createBatchRecord(batchRecord);
  }
);

interface CreateBatchRecordWithLinksPayload {
  batchRecord: BatchRecord;
  fillingBatchIds: string[];
  doughBatchIds: string[];
}

/**
 * Create a new batch record with linked filling and dough batches
 * Used by the Product wizard in Workflow page
 */
export const createBatchRecordWithLinksAsync = createAsyncThunk(
  'batchRecords/createBatchRecordWithLinks',
  async (payload: CreateBatchRecordWithLinksPayload) => {
    const { batchRecord, fillingBatchIds, doughBatchIds } = payload;

    console.log('[createBatchRecordWithLinksAsync] Starting batch record creation');
    console.log('[createBatchRecordWithLinksAsync] Filling batches to link:', fillingBatchIds.length);
    console.log('[createBatchRecordWithLinksAsync] Dough batches to link:', doughBatchIds.length);

    // First, create the batch record to get the real ID
    const createdBatchRecord = await batchRecordApiService.createBatchRecord(batchRecord);
    const realBatchRecordId = createdBatchRecord.id;

    console.log('[createBatchRecordWithLinksAsync] Batch record created with ID:', realBatchRecordId);

    // Create filling batch links
    if (fillingBatchIds.length > 0) {
      console.log('[createBatchRecordWithLinksAsync] Creating filling batch links...');
      await Promise.all(
        fillingBatchIds.map(fillingBatchId =>
          batchRecordFillingBatchApiService.createBatchRecordFillingBatch({
            batchRecordId: realBatchRecordId,
            fillingBatchId
          })
        )
      );
      console.log('[createBatchRecordWithLinksAsync] Filling batch links created');
    }

    // Create dough batch links
    if (doughBatchIds.length > 0) {
      console.log('[createBatchRecordWithLinksAsync] Creating dough batch links...');
      await Promise.all(
        doughBatchIds.map(doughBatchId =>
          batchRecordDoughBatchApiService.createBatchRecordDoughBatch({
            batchRecordId: realBatchRecordId,
            doughBatchId
          })
        )
      );
      console.log('[createBatchRecordWithLinksAsync] Dough batch links created');
    }

    console.log('[createBatchRecordWithLinksAsync] Batch record creation complete');
    return createdBatchRecord;
  }
);

/**
 * Update an existing batch record
 */
export const updateBatchRecordAsync = createAsyncThunk(
  'batchRecords/updateBatchRecord',
  async (batchRecord: BatchRecord) => {
    return await batchRecordApiService.updateBatchRecord(batchRecord);
  }
);

/**
 * Delete a batch record
 */
export const deleteBatchRecordAsync = createAsyncThunk(
  'batchRecords/deleteBatchRecord',
  async (id: string) => {
    await batchRecordApiService.deleteBatchRecord(id);
    return id;
  }
);

/**
 * Fetch a single batch record by ID
 */
export const fetchBatchRecordById = createAsyncThunk(
  'batchRecords/fetchBatchRecordById',
  async (id: string) => {
    return await batchRecordApiService.fetchBatchRecordById(id);
  }
);

// Initial state
const initialState: BatchRecordsState & {
  loading: boolean;
  error: string | null;
} = {
  records: [],
  loading: false,
  error: null,
};

const batchRecordsSlice = createSlice({
  name: 'batchRecords',
  initialState,
  reducers: {
    // Keep synchronous actions for backward compatibility (deprecated)
    addBatchRecord: (state, action: PayloadAction<BatchRecord>) => {
      state.records.push(action.payload);
    },
    updateBatchRecord: (state, action: PayloadAction<BatchRecord>) => {
      const index = state.records.findIndex(record => record.id === action.payload.id);
      if (index !== -1) {
        state.records[index] = action.payload;
      }
    },
    deleteBatchRecord: (state, action: PayloadAction<string>) => {
      state.records = state.records.filter(record => record.id !== action.payload);
    }
  },
  extraReducers: (builder) => {
    // Fetch batch records
    builder
      .addCase(fetchBatchRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBatchRecords.fulfilled, (state, action) => {
        state.loading = false;
        state.records = action.payload;
      })
      .addCase(fetchBatchRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch batch records';
      });

    // Create batch record
    builder
      .addCase(createBatchRecordAsync.fulfilled, (state, action) => {
        state.records.push(action.payload);
      })
      .addCase(createBatchRecordAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create batch record';
      });

    // Create batch record with links (Product wizard)
    builder
      .addCase(createBatchRecordWithLinksAsync.fulfilled, (state, action) => {
        state.records.push(action.payload);
      })
      .addCase(createBatchRecordWithLinksAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create batch record with links';
      });

    // Update batch record
    builder
      .addCase(updateBatchRecordAsync.fulfilled, (state, action) => {
        const index = state.records.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.records[index] = action.payload;
        }
      })
      .addCase(updateBatchRecordAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update batch record';
      });

    // Delete batch record
    builder
      .addCase(deleteBatchRecordAsync.fulfilled, (state, action) => {
        state.records = state.records.filter(r => r.id !== action.payload);
      })
      .addCase(deleteBatchRecordAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete batch record';
      });

    // Fetch batch record by ID
    builder
      .addCase(fetchBatchRecordById.fulfilled, (state, action) => {
        // Check if record already exists to prevent duplicates
        const existingIndex = state.records.findIndex(r => r.id === action.payload.id);
        if (existingIndex === -1) {
          state.records.push(action.payload);
        } else {
          // Update existing record if found
          state.records[existingIndex] = action.payload;
        }
      })
      .addCase(fetchBatchRecordById.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch batch record by ID';
      });
  }
});

export const { addBatchRecord, updateBatchRecord, deleteBatchRecord } = batchRecordsSlice.actions;
export default batchRecordsSlice.reducer;
